import { useCallback, useEffect, useState } from 'react';

import { Api } from '../../api/api';

import type { ChildHomeState } from './ChildHomeState';

/** Mirrors android/…/Screens/ChildHome/ChildHomeViewModel.kt. */
export function useChildHome(nickname: string) {
  const [state, setState] = useState<ChildHomeState>({
    nickname,
    isLoading: true,
    island: null,
    today: null,
    error: null,
  });

  const load = useCallback(async (): Promise<void> => {
    setState((current) => ({ ...current, isLoading: true, error: null }));

    // Both together: the screen needs the balances and whether there is a training to come back to, and
    // one failing must not hide the other.
    const [result, today] = await Promise.all([Api.readIsland(), Api.readToday()]);

    if (result.ok) {
      setState((current) => ({
        ...current,
        isLoading: false,
        island: result.value,
        today: today.ok ? today.value : null,
      }));

      return;
    }

    // A 403 in child mode means the client routed a parent endpoint here. That is a defect, not a state to
    // render to a child (ADR-0006), so it is reported and shown as nothing.
    const isRoutingDefect = result.error.kind === 'accessDenied';

    if (isRoutingDefect) reportAccessDeniedInChildMode('child-home');

    setState((current) => ({
      ...current,
      isLoading: false,
      error: isRoutingDefect ? null : result.error.message,
    }));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setState((current) => ({ ...current, nickname }));
  }, [nickname]);

  return { state, load };
}

/**
 * Mirrors `Platform/Diagnostics.kt`.
 *
 * A 403 in child mode is always a routing defect, and it has two shapes: a parent endpoint reached with a
 * child token, or a child endpoint reached after the child token was cleared. Both mean the client sent a
 * request the shell should have made impossible, so both are reported rather than rendered.
 */
export function reportAccessDeniedInChildMode(context: string): void {
  // eslint-disable-next-line no-console
  console.error(`[routing] Access denied in child mode at ${context}: the wrong token reached the API.`);
}
